/*
  customsirge_pdp_benefits_hero.js
  Purpose:  Swap the pre-rendered per-variant can image when the buy box's
            variant changes, per decision 0032 (client answers) Q1 — "can
            image is metafield-driven per product AND variant". All variant
            images are pre-rendered server-side as hidden
            [data-customsirge-pdp-benefits-hero-can-frame][data-variant-id]
            children of .customsirge-pdp-benefits-hero__can-track, so this
            file only toggles the [hidden] attribute — no fetch, no re-render.
  Spec:     .claude/knowledge/extraction/figma/sections/pdp_benefits_hero/BRIEF.md
            "Client answers" Q1 — "Listen for customsirge:variant-change
            (dispatched by pdp_main on its root, detail {variant})."
  Data:     reads only the custom event's `detail.variant.id` — no section or
            product state of its own (CONVENTIONS #7 applies to this
            component the same as a kit snippet: everything it needs arrives
            on the event).
  Renders:  nothing — toggles [hidden] on frames the section already rendered,
            and writes two custom properties for the headline (below).
  Owns:     Customsirge component 'pdp-benefits-hero'. No CSS namespace (the
            [hidden] attribute is the native browser default, display:none —
            see assets/customsirge_pdp_benefits_hero.css .can-frame[hidden]).
  Owner:    builder(pdp_benefits_hero), 2026-09-15
  Updated:  2026-09-28 — glow-up mobile v2: callouts-on-scroll pin driver
            (see CALLOUTS ON SCROLL below); same registered component.

  HEADLINE PAIRING (client instruction 2026-09-25) — word 2 sits under word 1
  and word 4 beside word 3, and each pair now hangs off its partner's WIDTH
  rather than off the section's edges:

    word 2   its right edge at 65% of word 1's width
    word 4   its left edge at 70% of word 3's width

  RATIOS ARE CSS, NOT CODE — both numbers live as custom properties on the
  section and are re-read on every measure:

    --customsirge-pdp-benefits-hero-word2-ratio
    --customsirge-pdp-benefits-hero-word4-ratio

  Declared with their defaults in assets/customsirge_pdp_benefits_hero.css, so
  fine-tuning is one number in a stylesheet — and can differ per breakpoint, or
  per instance through the section's own {% style %} block, without editing
  this file. Either "70%" or "0.7" parses; a value outside 0-1, or one that is
  not a number at all, falls back to the default instead of throwing the
  headline off screen.

  That cannot be CSS. The four words are absolutely positioned siblings, and a
  percentage in `left` resolves against the CONTAINING BLOCK (the section), not
  against another element — there is no selector that reads a sibling's width.
  So this file measures and writes --…-word2-left / --…-word4-left, and the
  stylesheet keeps the old literal offsets as the var() fallback: with this
  script blocked or still parsing, the headline lands where it always did
  instead of collapsing into a corner.

  Measured after `document.fonts.ready`, because the widths that drive both
  numbers are Anton's, not the fallback's — running earlier pairs the words to
  a face that is about to be replaced. Re-measured on resize, since every
  offset in this section scales with the viewport.

  CALLOUTS ON SCROLL (2026-09-28, glow-up mobile v2) — below 750px, when
  motion is allowed and at least three callouts are showing (two pairs), the
  root gets `customsirge-pdp-benefits-hero--pin`: the card sticks under the
  header while the root grows by the spacer's 100vh (CSS), and this file
  writes the scroll progress through that range to
  --customsirge-pdp-benefits-hero-swap (0..1) on the root — rAF-throttled,
  passive scroll listener, reads only getBoundingClientRect. Each showing
  callout (not `__item--hide-mobile`) is tagged data-customsirge-pdp-benefits-
  hero-pair = a (first two) / b (the rest) and --customsirge-pdp-benefits-hero-
  row = 1 / 2, so the stylesheet can stack the pairs on the same two rows and
  crossfade them over 0.35 -> 0.65. Reduced motion, a desktop viewport or
  fewer than three showing callouts: nothing is written and the callouts stack.

  customsirge: pdp_main is not built yet in this run (no
  sections/customsirge_pdp_main.liquid at time of writing, per
  plans/sections.json) — this listener implements the documented contract
  (architecture/metafields.md "Q1 — band colour + can are metafield-driven")
  ahead of it and cannot be exercised end-to-end until pdp_main dispatches
  the event. Listens on `document` rather than a named root element since the
  brief only specifies the event name + detail shape, not a dispatch target
  this file can assume exists.
*/
(function () {
  'use strict';

  if (!window.Customsirge || typeof window.Customsirge.register !== 'function') {
    console.error('[customsirge] pdp_benefits_hero.js requires customsirge_kit.js to load first');
    return;
  }

  var Customsirge = window.Customsirge;

  Customsirge.register('pdp-benefits-hero', {
    selector: '[data-customsirge-pdp-benefits-hero]',
    init: function (el) {
      /* Defaults only. The live values come from two custom properties on the
         section, read fresh on every measure, so the ratios are tunable in CSS
         (per breakpoint, per instance, or live in devtools) without touching
         this file — see the RATIOS note in the header. */
      var WORD_2_OF_WORD_1 = 0.65;
      var WORD_4_OF_WORD_3 = 0.7;

      function ratio(prop, fallback) {
        var raw = getComputedStyle(el).getPropertyValue(prop);
        var n = parseFloat(raw);
        /* A percentage reads as "65%" and a bare fraction as "0.65"; both are
           useful to write by hand, so accept either. Anything unparseable or
           out of range falls back rather than throwing the headline off-screen. */
        if (!isFinite(n)) return fallback;
        if (raw.indexOf('%') !== -1) n = n / 100;
        if (n < 0 || n > 1) return fallback;
        return n;
      }
      var resizeTimer = null;
      var wordObserver = null;

      function w1El() {
        return el.querySelector('.customsirge-pdp-benefits-hero__word--1');
      }
      function w3El() {
        return el.querySelector('.customsirge-pdp-benefits-hero__word--3');
      }

      function pairWords() {
        var w1 = el.querySelector('.customsirge-pdp-benefits-hero__word--1');
        var w2 = el.querySelector('.customsirge-pdp-benefits-hero__word--2');
        var w3 = el.querySelector('.customsirge-pdp-benefits-hero__word--3');
        var w4 = el.querySelector('.customsirge-pdp-benefits-hero__word--4');
        var style = el.style;

        /* Below 750px the words are an inline sentence, not a composition —
           nothing to pair, and a stale value from a wider viewport would move
           an element the mobile rules do not position at all. */
        if (!window.matchMedia('(min-width: 750px)').matches) {
          style.removeProperty('--customsirge-pdp-benefits-hero-word2-left');
          style.removeProperty('--customsirge-pdp-benefits-hero-word4-left');
          style.removeProperty('--customsirge-pdp-benefits-hero-word4-right');
          style.removeProperty('--customsirge-pdp-benefits-hero-fit');
          return;
        }

        var root = el.getBoundingClientRect();

        /* 2026-10-19 FIT: per-line headlines ("More Thought", "Your Plans.")
           are wider than the drawn words, and word 1 grows leftward off the
           section. Measure at full size, then shrink all four words by the one
           factor that keeps words 1 and 3 inside the section. */
        style.setProperty('--customsirge-pdp-benefits-hero-fit', '1');
        var fitInset = root.width * (20 / 1440);
        var fit = 1;
        if (w1) {
          var f1 = w1.getBoundingClientRect();
          if (f1.width > 0) fit = Math.min(fit, (f1.right - root.left - fitInset) / f1.width);
        }
        if (w3) {
          var f3 = w3.getBoundingClientRect();
          if (f3.width > 0) fit = Math.min(fit, (root.right - fitInset - f3.left) / f3.width);
        }
        fit = Math.max(0.5, Math.min(1, fit));
        style.setProperty('--customsirge-pdp-benefits-hero-fit', fit.toFixed(3));

        /* A floor and a ceiling, not the frame's drawn insets: the pairing is a
           position, and clamping it back to where the Figma literals sat would
           just undo it. This only stops a word LEAVING the section — which it
           can now ask to do, since type stopped scaling below 2000px while the
           offsets kept scaling (at 1024 the unclamped word 4 started 15px past
           the right edge). 20px of the drawn frame, scaled. */
        var inset = root.width * (20 / 1440);

        if (w1 && w2) {
          var r1 = w1.getBoundingClientRect();
          var r2 = w2.getBoundingClientRect();
          /* right edge of word 2 == 65% across word 1, so its LEFT is that
             point minus word 2's own width. */
          var left2 =
            r1.left - root.left +
            r1.width * ratio('--customsirge-pdp-benefits-hero-word2-ratio', WORD_2_OF_WORD_1) -
            r2.width;
          left2 = Math.max(inset, Math.min(left2, root.width - inset - r2.width));
          style.setProperty('--customsirge-pdp-benefits-hero-word2-left', left2.toFixed(2) + 'px');
        }

        if (w3 && w4) {
          var r3 = w3.getBoundingClientRect();
          var r4 = w4.getBoundingClientRect();
          var left4 =
            r3.left - root.left +
            r3.width * ratio('--customsirge-pdp-benefits-hero-word4-ratio', WORD_4_OF_WORD_3);
          left4 = Math.max(inset, Math.min(left4, root.width - inset - r4.width));
          style.setProperty('--customsirge-pdp-benefits-hero-word4-left', left4.toFixed(2) + 'px');
          /* left and right together on an auto-width box would stretch it
             between the two; the right anchor is the fallback's job only. */
          style.setProperty('--customsirge-pdp-benefits-hero-word4-right', 'auto');
        }
      }

      function schedulePair() {
        if (window.requestAnimationFrame) window.requestAnimationFrame(pairWords);
        else pairWords();
      }

      function onResize() {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(schedulePair, 100);
      }

      schedulePair();
      /* Both partners drive the numbers, so watch them rather than guess when
         they settle: the web font swapping in resizes word 1 and word 3, and so
         does a merchant editing the words in the theme editor. fonts.ready
         alone fired before the relayout on the first build of this and paired
         the words to fallback-font widths. */
      if (window.ResizeObserver) {
        wordObserver = new ResizeObserver(schedulePair);
        [w1El(), w3El()].forEach(function (n) {
          if (n) wordObserver.observe(n);
        });
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedulePair);
      window.addEventListener('resize', onResize);

      function showVariant(variantId) {
        if (variantId === undefined || variantId === null) return;
        var frames = el.querySelectorAll('[data-customsirge-pdp-benefits-hero-can-frame]');
        Array.prototype.forEach.call(frames, function (frame) {
          frame.hidden = String(frame.dataset.variantId) !== String(variantId);
        });
      }

      function onVariantChange(event) {
        var variant = event && event.detail && event.detail.variant;
        if (!variant || variant.id === undefined) return;
        showVariant(variant.id);
      }

      document.addEventListener('customsirge:variant-change', onVariantChange);

      /* ---- callouts on scroll (mobile pin) — see the file header ---- */
      var PIN_CLASS = 'customsirge-pdp-benefits-hero--pin';
      var PAIR_ATTR = 'data-customsirge-pdp-benefits-hero-pair';
      var ROW_PROP = '--customsirge-pdp-benefits-hero-row';
      var SWAP_PROP = '--customsirge-pdp-benefits-hero-swap';
      var PIN_TOP_PROP = '--customsirge-pdp-benefits-hero-pin-top';
      var card = el.querySelector('.customsirge-pdp-benefits-hero__card');
      var pinQuery = window.matchMedia
        ? window.matchMedia('(max-width: 749px) and (prefers-reduced-motion: no-preference)')
        : null;
      var pinned = false;
      var pinTicking = false;

      function showingItems() {
        var items = el.querySelectorAll('.customsirge-pdp-benefits-hero__item');
        return Array.prototype.filter.call(items, function (item) {
          return !item.classList.contains('customsirge-pdp-benefits-hero__item--hide-mobile');
        });
      }

      function updatePin() {
        pinTicking = false;
        if (!pinned) return;
        var rect = el.getBoundingClientRect();
        /* A card taller than the viewport under the header would pin with its
           callouts — the part that swaps — cut off at the bottom. Pull its
           sticky top up by the excess so the bottom edge is what stays on
           screen; the header's own height is the ceiling. */
        var headerH = parseFloat(getComputedStyle(el).getPropertyValue('--customsirge-header-h')) || 0;
        var pinTop = Math.min(headerH, window.innerHeight - card.offsetHeight);
        el.style.setProperty(PIN_TOP_PROP, Math.round(pinTop) + 'px');
        /* the card's resolved sticky top — progress is measured from where
           the card actually sticks, not from 0 */
        var top = parseFloat(getComputedStyle(card).top) || 0;
        var range = el.offsetHeight - card.offsetHeight;
        var p = range > 0 ? (top - rect.top) / range : 0;
        p = Math.max(0, Math.min(1, p));
        el.style.setProperty(SWAP_PROP, p.toFixed(4));
      }

      function onPinScroll() {
        if (pinTicking) return;
        pinTicking = true;
        if (window.requestAnimationFrame) window.requestAnimationFrame(updatePin);
        else updatePin();
      }

      function pinOff() {
        if (!pinned) return;
        pinned = false;
        window.removeEventListener('scroll', onPinScroll);
        window.removeEventListener('resize', onPinScroll);
        el.classList.remove(PIN_CLASS);
        el.style.removeProperty(SWAP_PROP);
        el.style.removeProperty(PIN_TOP_PROP);
        Array.prototype.forEach.call(el.querySelectorAll('[' + PAIR_ATTR + ']'), function (item) {
          item.removeAttribute(PAIR_ATTR);
          item.style.removeProperty(ROW_PROP);
        });
      }

      function pinSetup() {
        pinOff();
        if (!card || !pinQuery || !pinQuery.matches) return;
        var items = showingItems();
        if (items.length < 3) return; // one pair: nothing to swap to
        items.forEach(function (item, i) {
          item.setAttribute(PAIR_ATTR, i < 2 ? 'a' : 'b');
          item.style.setProperty(ROW_PROP, String((i % 2) + 1));
        });
        pinned = true;
        el.classList.add(PIN_CLASS);
        window.addEventListener('scroll', onPinScroll, { passive: true });
        window.addEventListener('resize', onPinScroll);
        onPinScroll();
      }

      pinSetup();
      if (pinQuery) {
        if (pinQuery.addEventListener) pinQuery.addEventListener('change', pinSetup);
        else if (pinQuery.addListener) pinQuery.addListener(pinSetup);
      }

      return function () {
        if (resizeTimer) clearTimeout(resizeTimer);
        if (wordObserver) wordObserver.disconnect();
        window.removeEventListener('resize', onResize);
        document.removeEventListener('customsirge:variant-change', onVariantChange);
        if (pinQuery) {
          if (pinQuery.removeEventListener) pinQuery.removeEventListener('change', pinSetup);
          else if (pinQuery.removeListener) pinQuery.removeListener(pinSetup);
        }
        pinOff();
      };
    },
  });
})();
